'use client'

import { useState, useEffect } from 'react'
import styles from './TerminalTabs.module.css'
import { ConnectorManager } from '../../ConnectorManager'
import { useSearchParams } from 'next/navigation'

interface OrderHistoryItem {
  symbol: string
  orderId: number
  orderListId: number
  clientOrderId: string
  price: string
  origQty: string
  executedQty: string
  cummulativeQuoteQty: string
  status: string
  timeInForce: string
  type: string
  side: string
  stopPrice: string
  icebergQty: string
  time: number
  updateTime: number
  isWorking: boolean
  origQuoteOrderQty: string
}

// Common trading pairs for the dropdown
const COMMON_PAIRS = [
  'BTCUSDT',
  'ETHUSDT',
  'BNBUSDT',
  'ADAUSDT',
  'DOGEUSDT',
  'XRPUSDT',
  'DOTUSDT',
  'UNIUSDT',
  'LTCUSDT',
  'LINKUSDT',
  'BTCBUSD',
  'ETHBTC',
  'BNBBTC'
]

export default function OrderHistory() {
  const searchParams = useSearchParams()
  // const urlSymbol = searchParams?.get('symbol');

  // Get symbol from URL path instead of query parameters
  const pathname = typeof window !== 'undefined' ? window.location.pathname : ''
  const pathSymbol = pathname.split('/').pop()?.toUpperCase()

  const urlSymbol = pathSymbol || searchParams?.get('symbol')

  const [orders, setOrders] = useState<OrderHistoryItem[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedSymbol, setSelectedSymbol] = useState(urlSymbol || 'BTCUSDT')
  const [customSymbol, setCustomSymbol] = useState('')
  const [showCustomInput, setShowCustomInput] = useState(false)
  const connectorManager = ConnectorManager.getInstance()

  useEffect(() => {
    fetchOrderHistory(selectedSymbol)
  }, [selectedSymbol])

  const fetchOrderHistory = async (symbol: string) => {
    setIsLoading(true)
    setError(null)

    try {
      // Get the Binance connector
      const binanceConnector = connectorManager.getConnector('binance')

      if (!binanceConnector) {
        throw new Error('Binance connector not found or not connected')
      }

      // Fetch order history from the connector with the specified symbol
      const ordersData = await binanceConnector.getOrderHistory(symbol)

      // Sort orders by time, most recent first
      const sortedOrders = [...ordersData].sort((a, b) => b.time - a.time)
      setOrders(sortedOrders)
    } catch (err: any) {
      console.error('Error fetching order history:', err)
      setError(
        err.message ||
          'Failed to fetch order history. Please ensure you are connected to Binance.'
      )
    } finally {
      setIsLoading(false)
    }
  }

  const handleSymbolChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value
    if (value === 'custom') {
      setShowCustomInput(true)
    } else {
      setShowCustomInput(false)
      setSelectedSymbol(value)
    }
  }

  const handleCustomSymbolSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (customSymbol) {
      setSelectedSymbol(customSymbol.toUpperCase())
      setCustomSymbol('')
      setShowCustomInput(false)
    }
  }

  if (isLoading)
    return <div className={styles.loadingState}>Loading order history...</div>
  if (error) return <div className={styles.errorState}>{error}</div>

  return (
    <div className={styles.ordersContainer}>
      <div className={styles.symbolSelector}>
        <div className={styles.selectorWrapper}>
          <select
            value={
              showCustomInput
                ? 'custom'
                : COMMON_PAIRS.includes(selectedSymbol)
                  ? selectedSymbol
                  : 'custom'
            }
            onChange={handleSymbolChange}
            className={styles.symbolDropdown}
          >
            {COMMON_PAIRS.map(pair => (
              <option key={pair} value={pair}>
                {pair}
              </option>
            ))}
            <option value="custom">Custom Pair</option>
          </select>

          {(showCustomInput || !COMMON_PAIRS.includes(selectedSymbol)) && (
            <form
              onSubmit={handleCustomSymbolSubmit}
              className={styles.customSymbolForm}
            >
              <input
                type="text"
                value={
                  customSymbol ||
                  (!COMMON_PAIRS.includes(selectedSymbol) && !showCustomInput
                    ? selectedSymbol
                    : '')
                }
                onChange={e => setCustomSymbol(e.target.value)}
                placeholder="Enter symbol (e.g. SOLUSDT)"
                className={styles.customSymbolInput}
                autoFocus
              />
              <button type="submit" className={styles.submitButton}>
                Go
              </button>
            </form>
          )}
        </div>
        <div className={styles.currentSymbol}>
          {(() => {
            // Calculate metrics for the selected symbol
            const filledOrders = orders.filter(
              order => order.status === 'FILLED'
            )

            // Calculate total buy and sell quantities
            const buyOrders = filledOrders.filter(order => order.side === 'BUY')
            const sellOrders = filledOrders.filter(
              order => order.side === 'SELL'
            )

            const totalBuyQty = buyOrders.reduce(
              (sum, order) => sum + parseFloat(order.executedQty),
              0
            )
            const totalSellQty = sellOrders.reduce(
              (sum, order) => sum + parseFloat(order.executedQty),
              0
            )

            // Calculate total buy and sell values
            const totalBuyValue = buyOrders.reduce(
              (sum, order) => sum + parseFloat(order.cummulativeQuoteQty),
              0
            )
            const totalSellValue = sellOrders.reduce(
              (sum, order) => sum + parseFloat(order.cummulativeQuoteQty),
              0
            )

            // Calculate average prices
            const avgBuyPrice =
              totalBuyQty > 0 ? totalBuyValue / totalBuyQty : 0
            const avgSellPrice =
              totalSellQty > 0 ? totalSellValue / totalSellQty : 0

            // Calculate overall average price
            const totalQty = totalBuyQty + totalSellQty
            const totalValue = totalBuyValue + totalSellValue
            const avgPrice = totalQty > 0 ? totalValue / totalQty : 0

            return (
              <>
                Current: <strong>{selectedSymbol}</strong>
                {filledOrders.length > 0 && (
                  <>
                    {' | '}
                    Total Buy:{' '}
                    <strong className={styles.buyOrder}>
                      {totalBuyQty.toFixed(4)}
                    </strong>{' '}
                    (${totalBuyValue.toFixed(2)}){' | '}
                    Total Sell:{' '}
                    <strong className={styles.sellOrder}>
                      {totalSellQty.toFixed(4)}
                    </strong>{' '}
                    (${totalSellValue.toFixed(2)}){' | '}
                    Avg Price:{' '}
                    <strong>
                      ${avgPrice.toFixed(avgPrice < 0.01 ? 8 : 2)}
                    </strong>
                    {' | '}
                    Net:{' '}
                    <strong
                      className={
                        totalBuyQty > totalSellQty
                          ? styles.buyOrder
                          : styles.sellOrder
                      }
                    >
                      {Math.abs(totalBuyQty - totalSellQty).toFixed(4)}
                    </strong>
                  </>
                )}
              </>
            )
          })()}
        </div>
      </div>

      {orders.length === 0 ? (
        <div className={styles.emptyState}>
          No order history found for {selectedSymbol}
        </div>
      ) : (
        <div className={styles.ordersTable}>
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Symbol</th>
                <th>Type</th>
                <th>Side</th>
                <th>Price</th>
                <th>Amount</th>
                <th>Filled</th>
                <th>Total</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {orders.map(order => {
                const date = new Date(order.time)
                const formattedDate = date.toLocaleString()

                // For MARKET orders, price might be 0, so calculate from cummulativeQuoteQty
                let price = parseFloat(order.price)
                const amount = parseFloat(order.origQty)
                const filled = parseFloat(order.executedQty)

                // Calculate total using cummulativeQuoteQty for more accuracy
                const total = parseFloat(order.cummulativeQuoteQty)

                // For MARKET orders, calculate the average price
                if (order.type === 'MARKET' && price === 0 && filled > 0) {
                  price = total / filled
                }

                return (
                  <tr key={order.orderId}>
                    <td>{formattedDate}</td>
                    <td>{order.symbol}</td>
                    <td>{order.type}</td>
                    <td
                      className={
                        order.side === 'BUY'
                          ? styles.buyOrder
                          : styles.sellOrder
                      }
                    >
                      {order.side}
                    </td>
                    <td>${price.toFixed(price < 0.01 ? 8 : 2)}</td>
                    <td>{amount.toFixed(8)}</td>
                    <td>{filled.toFixed(8)}</td>
                    <td>${total.toFixed(2)}</td>
                    <td
                      className={
                        order.status === 'FILLED'
                          ? styles.statusFilled
                          : order.status === 'CANCELED'
                            ? styles.statusCanceled
                            : styles.statusOther
                      }
                    >
                      {order.status}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
